// @ts-nocheck

// Types for SiriWave options
interface SiriWaveOptions {
	container: HTMLElement;
	style?: 'ios' | 'ios9';
	ratio?: number;
	speed?: number;
	amplitude?: number;
	frequency?: number;
	color?: string;
	cover?: boolean;
	width?: number;
	height?: number;
	autostart?: boolean;
	pixelDepth?: number;
	lerpSpeed?: number;
	curveDefinition?: CurveDefinition[];
	colors?: string[];
}

interface CurveDefinition {
	attenuation?: number;
	lineWidth?: number;
	opacity?: number;
	color?: string;
	supportLine?: boolean;
}

// Helper utility function
function __rest<T extends object, K extends keyof T>(source: T, exclude: K[]): Omit<T, K> {
	const target: Partial<T> = {};
	for (const prop in source) {
		if (Object.prototype.hasOwnProperty.call(source, prop) && exclude.indexOf(prop as K) < 0) {
			target[prop as keyof T] = source[prop as keyof T];
		}
	}
	if (source != null && typeof Object.getOwnPropertySymbols === "function") {
		for (const sym of Object.getOwnPropertySymbols(source)) {
			if (exclude.indexOf(sym as unknown as K) < 0 && Object.prototype.propertyIsEnumerable.call(source, sym)) {
				target[sym as unknown as keyof T] = source[sym as unknown as keyof T];
			}
		}
	}
	return target as Omit<T, K>;
}

// Classic iOS curve implementation
class ClassicCurve {
	private ATT_FACTOR = 4;
	private GRAPH_X = 2;
	private AMPLITUDE_FACTOR = 0.6;
	private ctrl: SiriWave;
	private definition: CurveDefinition;

	constructor(ctrl: SiriWave, definition: CurveDefinition) {
		this.ctrl = ctrl;
		this.definition = definition;
	}

	private globalAttFn(x: number): number {
		return Math.pow(this.ATT_FACTOR / (this.ATT_FACTOR + Math.pow(x, this.ATT_FACTOR)), this.ATT_FACTOR);
	}

	private xPos(i: number): number {
		return this.ctrl.width * ((i + this.GRAPH_X) / (this.GRAPH_X * 2));
	}

	private yPos(i: number): number {
		return (this.AMPLITUDE_FACTOR *
			(this.globalAttFn(i) *
				(this.ctrl.heightMax * this.ctrl.amplitude) *
				(1 / (this.definition.attenuation || 1)) *
				Math.sin(this.ctrl.opt.frequency! * i - this.ctrl.phase)));
	}

	public draw(): void {
		const ctx = this.ctrl.ctx;
		ctx.moveTo(0, 0);
		ctx.beginPath();
		const color = this.ctrl.color.replace(/rgb\(/g, "").replace(/\)/g, "");
		ctx.strokeStyle = `rgba(${color},${this.definition.opacity})`;
		ctx.lineWidth = this.definition.lineWidth || 1;

		// Cycle the graph from -X to +X every PX_DEPTH and draw the line
		for (let i = -this.GRAPH_X; i <= this.GRAPH_X; i += this.ctrl.opt.pixelDepth!) {
			ctx.lineTo(this.xPos(i), this.ctrl.heightMax + this.yPos(i));
		}
		ctx.stroke();
	}

	public static getDefinition(): CurveDefinition[] {
		return [
			{
				attenuation: -2,
				lineWidth: 1,
				opacity: 0.1,
			},
			{
				attenuation: -6,
				lineWidth: 1,
				opacity: 0.2,
			},
			{
				attenuation: 4,
				lineWidth: 1,
				opacity: 0.4,
			},
			{
				attenuation: 2,
				lineWidth: 1,
				opacity: 0.6,
			},
			{
				attenuation: 1,
				lineWidth: 1.5,
				opacity: 1,
			},
		];
	}
}

// iOS9 curve implementation
class iOS9Curve {
	private GRAPH_X = 25;
	private AMPLITUDE_FACTOR = 0.8;
	private SPEED_FACTOR = 1;
	private DEAD_PX = 2;
	private ATT_FACTOR = 4;
	private DESPAWN_FACTOR = 0.02;
	private NOOFCURVES_RANGES = [2, 5];
	private AMPLITUDE_RANGES = [0.3, 1];
	private OFFSET_RANGES = [-3, 3];
	private WIDTH_RANGES = [1, 3];
	private SPEED_RANGES = [0.5, 1];
	private DESPAWN_TIMEOUT_RANGES = [500, 2000];

	private ctrl: SiriWave;
	public definition: CurveDefinition;
	private noOfCurves: number = 0;
	private spawnAt: number = 0;
	private prevMaxY: number = 0;
	private phases: number[] = [];
	private offsets: number[] = [];
	private speeds: number[] = [];
	private finalAmplitudes: number[] = [];
	private widths: number[] = [];
	private amplitudes: number[] = [];
	private despawnTimeouts: number[] = [];
	private verses: number[] = [];

	constructor(ctrl: SiriWave, definition: CurveDefinition) {
		this.ctrl = ctrl;
		this.definition = definition;
	}

	private getRandomRange(e: number[]): number {
		return e[0] + Math.random() * (e[1] - e[0]);
	}

	private spawnSingle(ci: number): void {
		this.phases[ci] = 0;
		this.amplitudes[ci] = 0;
		this.despawnTimeouts[ci] = this.getRandomRange(this.DESPAWN_TIMEOUT_RANGES);
		this.offsets[ci] = this.getRandomRange(this.OFFSET_RANGES);
		this.speeds[ci] = this.getRandomRange(this.SPEED_RANGES);
		this.finalAmplitudes[ci] = this.getRandomRange(this.AMPLITUDE_RANGES);
		this.widths[ci] = this.getRandomRange(this.WIDTH_RANGES);
		this.verses[ci] = this.getRandomRange([-1, 1]);
	}

	private getEmptyArray(count: number): number[] {
		return new Array(count);
	}

	private spawn(): void {
		this.spawnAt = Date.now();
		this.noOfCurves = Math.floor(this.getRandomRange(this.NOOFCURVES_RANGES));
		this.phases = this.getEmptyArray(this.noOfCurves);
		this.offsets = this.getEmptyArray(this.noOfCurves);
		this.speeds = this.getEmptyArray(this.noOfCurves);
		this.finalAmplitudes = this.getEmptyArray(this.noOfCurves);
		this.widths = this.getEmptyArray(this.noOfCurves);
		this.amplitudes = this.getEmptyArray(this.noOfCurves);
		this.despawnTimeouts = this.getEmptyArray(this.noOfCurves);
		this.verses = this.getEmptyArray(this.noOfCurves);

		for (let ci = 0; ci < this.noOfCurves; ci++) {
			this.spawnSingle(ci);
		}
	}

	private globalAttFn(x: number): number {
		return Math.pow(this.ATT_FACTOR / (this.ATT_FACTOR + Math.pow(x, 2)), this.ATT_FACTOR);
	}

	private sin(x: number, phase: number): number {
		return Math.sin(x - phase);
	}

	private yRelativePos(i: number): number {
		let y = 0;
		for (let ci = 0; ci < this.noOfCurves; ci++) {
			// Generate a static T so that each curve is distant from each other
			let t = 4 * (-1 + (ci / (this.noOfCurves - 1)) * 2);
			// but add a dynamic offset
			t += this.offsets[ci];
			const k = 1 / this.widths[ci];
			const x = i * k - t;
			y += Math.abs(this.amplitudes[ci] * this.sin(this.verses[ci] * x, this.phases[ci]) * this.globalAttFn(x));
		}
		// Divide for NoOfCurves so that y <= 1
		return y / this.noOfCurves;
	}

	private yPos(i: number): number {
		return (this.AMPLITUDE_FACTOR *
			this.ctrl.heightMax *
			this.ctrl.amplitude *
			this.yRelativePos(i) *
			this.globalAttFn((i / this.GRAPH_X) * 2));
	}

	private xPos(i: number): number {
		return this.ctrl.width * ((i + this.GRAPH_X) / (this.GRAPH_X * 2));
	}

	private drawSupportLine(): void {
		const ctx = this.ctrl.ctx;
		const coo: [number, number, number, number] = [0, this.ctrl.heightMax, this.ctrl.width, 1];
		const gradient = ctx.createLinearGradient.apply(ctx, coo);
		gradient.addColorStop(0, "transparent");
		gradient.addColorStop(0.1, "rgba(255,255,255,.5)");
		gradient.addColorStop(1 - 0.1 - 0.1, "rgba(255,255,255,.5)");
		gradient.addColorStop(1, "transparent");
		ctx.fillStyle = gradient;
		ctx.fillRect.apply(ctx, coo);
	}

	public draw(): null {
		const ctx = this.ctrl.ctx;
		ctx.globalAlpha = 0.7;
		ctx.globalCompositeOperation = "source-over";

		if (this.spawnAt === 0) {
			this.spawn();
		}

		if (this.definition.supportLine) {
			// Draw the support line
			this.drawSupportLine();
			return null;
		}

		for (let ci = 0; ci < this.noOfCurves; ci++) {
			if (this.spawnAt + this.despawnTimeouts[ci] <= Date.now()) {
				this.amplitudes[ci] -= this.DESPAWN_FACTOR;
			} else {
				this.amplitudes[ci] += this.DESPAWN_FACTOR;
			}
			this.amplitudes[ci] = Math.min(Math.max(this.amplitudes[ci], 1), this.finalAmplitudes[ci]);
			this.phases[ci] = (this.phases[ci] + this.ctrl.speed * this.speeds[ci] * this.SPEED_FACTOR) % (2 * Math.PI);
		}

		let maxY = -Infinity;

		// 恢复上下两个波形，但使用更好的透明度控制
		for (const sign of [1, -1]) {
			ctx.beginPath();
			for (let i = -this.GRAPH_X; i <= this.GRAPH_X; i += this.ctrl.opt.pixelDepth!) {
				const x = this.xPos(i);
				const y = this.yPos(i);
				ctx.lineTo(x, this.ctrl.heightMax - sign * y);
				maxY = Math.max(maxY, y);
			}
			ctx.closePath();

			// 使用透明度来避免重叠区域变白
			// 透明度设置得足够低，这样重叠部分就不会太亮
			ctx.fillStyle = `rgba(${this.definition.color}, 0.4)`;
			ctx.fill();
		}

		if (maxY < this.DEAD_PX && this.prevMaxY > maxY) {
			this.spawnAt = 0;
		}
		this.prevMaxY = maxY;
		return null;
	}

	public static getDefinition(customColors?: string[]): CurveDefinition[] {
		// 修改条件，只要有颜色就使用它们
		if (customColors && customColors.length > 0) {
			return [
				{
					color: "255,255,255",
					supportLine: true,
				},
				{
					// 添加透明度到颜色中
					color: `${customColors[0] || "15, 82, 169"}`,
					opacity: 0.9, // 添加透明度属性
				},
				{
					color: `${customColors[1] || customColors[0] || "173, 57, 76"}`,
					opacity: 0.9,
				},
				{
					color: `${customColors[2] || customColors[1] || customColors[0] || "48, 220, 155"}`,
					opacity: 0.9,
				},
				{
					color: `${customColors[3] || customColors[2] || customColors[1] || customColors[0] || "48, 220, 155"}`,
					opacity: 0.9,
				},
			];
		}

		// 原始默认颜色
		return [
			{
				color: "255,255,255",
				supportLine: true,
			},
			{
				// blue
				color: "15, 82, 169",
				opacity: 0.7,
			},
			{
				// red
				color: "173, 57, 76",
				opacity: 0.7,
			},
			{
				// green
				color: "48, 220, 155",
				opacity: 0.7,
			},
		];
	}
}

// Main SiriWave class
export default class SiriWave {
	// Properties
	private phase: number = 0;
	private run: boolean = false;
	private curves: (ClassicCurve | iOS9Curve)[] = [];
	private speed: number;
	private amplitude: number;
	private width: number;
	private height: number;
	public heightMax: number;
	public color: string;
	private interpolation: { [key: string]: number | null } = {
		speed: 0,
		amplitude: 0,
	};
	public canvas: HTMLCanvasElement | null;
	public ctx: CanvasRenderingContext2D;
	public opt: SiriWaveOptions;
	private animationFrameId?: number;
	private timeoutId?: NodeJS.Timeout;
	private customColors?: string[];

	constructor(options: SiriWaveOptions) {
		const { container, colors, ...rest } = options;

		// Store custom colors if provided
		this.customColors = colors?.map(color => {
			// Convert hex colors to RGB format if needed
			if (color.startsWith('#')) {
				const rgb = this.hex2rgb(color);
				return rgb || '';
			}
			return color;
		});

		const csStyle = window.getComputedStyle(container);

		this.opt = {
			container,
			style: "ios",
			ratio: window.devicePixelRatio || 1,
			speed: 0.2,
			amplitude: 1,
			frequency: 6,
			color: "#fff",
			cover: false,
			width: parseInt(csStyle.width.replace("px", ""), 10),
			height: parseInt(csStyle.height.replace("px", ""), 10),
			autostart: true,
			pixelDepth: 0.02,
			lerpSpeed: 0.1,
			...rest
		};

		/**
		 * Actual speed of the animation. Is not safe to change this value directly, use `setSpeed` instead.
		 */
		this.speed = Number(this.opt.speed);

		/**
		 * Actual amplitude of the animation. Is not safe to change this value directly, use `setAmplitude` instead.
		 */
		this.amplitude = Number(this.opt.amplitude);

		/**
		 * Width of the canvas multiplied by pixel ratio
		 */
		this.width = Number(this.opt.ratio! * this.opt.width!);

		/**
		 * Height of the canvas multiplied by pixel ratio
		 */
		this.height = Number(this.opt.ratio! * this.opt.height!);

		/**
		 * Maximum height for a single wave
		 */
		this.heightMax = Number(this.height / 2) - 6;

		/**
		 * Color of the wave (used in Classic iOS)
		 */
		this.color = `rgb(${this.hex2rgb(this.opt.color!)})`;

		/**
		 * An object containing controller variables that need to be interpolated
		 * to an another value before to be actually changed
		 */
		this.interpolation = {
			speed: this.speed,
			amplitude: this.amplitude,
		};

		/**
		 * Canvas DOM Element where curves will be drawn
		 */
		this.canvas = document.createElement("canvas");

		/**
		 * 2D Context from Canvas
		 */
		const ctx = this.canvas.getContext("2d");
		if (ctx === null) {
			throw new Error("Unable to create 2D Context");
		}
		this.ctx = ctx;

		// Set dimensions
		this.canvas.width = this.width;
		this.canvas.height = this.height;

		// By covering, we ensure the canvas is in the same size of the parent
		if (this.opt.cover === true) {
			this.canvas.style.width = this.canvas.style.height = "100%";
		} else {
			this.canvas.style.width = `${this.width / this.opt.ratio!}px`;
			this.canvas.style.height = `${this.height / this.opt.ratio!}px`;
		}

		// Instantiate all curves based on the style
		switch (this.opt.style) {
			case "ios9":
				this.curves = (this.opt.curveDefinition || iOS9Curve.getDefinition(this.customColors)).map(
					(def) => new iOS9Curve(this, def)
				);
				break;
			case "ios":
			default:
				this.curves = (this.opt.curveDefinition || ClassicCurve.getDefinition()).map(
					(def) => new ClassicCurve(this, def)
				);
				break;
		}

		// Attach to the container
		this.opt.container.appendChild(this.canvas);

		// Start the animation
		if (this.opt.autostart) {
			this.start();
		}
	}

	/**
	 * Convert an HEX color to RGB
	 */
	private hex2rgb(hex: string): string | null {
		// 确保处理前缀为 '#' 的颜色
		if (!hex.startsWith('#')) {
			// 如果已经是RGB格式 (如 "15, 82, 169")，直接返回
			if (hex.includes(',')) {
				return hex;
			}
			hex = '#' + hex;
		}

		const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
		hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		if (!result) {
			console.warn('Invalid hex color format:', hex);
			return "0,0,0"; // 返回黑色作为默认值
		}
		return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
	}

	private intLerp(v0: number, v1: number, t: number): number {
		return v0 * (1 - t) + v1 * t;
	}

	/**
	 * Interpolate a property to the value found in this.interpolation
	 */
	private lerp(propertyStr: string): number {
		const prop = this.interpolation[propertyStr];
		if (prop !== null) {
			this[propertyStr as keyof SiriWave] = this.intLerp(
				this[propertyStr as keyof SiriWave] as number,
				prop as number,
				this.opt.lerpSpeed!
			);
			if ((this[propertyStr as keyof SiriWave] as number) - (prop as number) === 0) {
				this.interpolation[propertyStr] = null;
			}
		}
		return this[propertyStr as keyof SiriWave] as number;
	}

	/**
	 * Clear the canvas
	 */
	private clear(): void {
		this.ctx.globalCompositeOperation = "destination-out";
		this.ctx.fillRect(0, 0, this.width, this.height);
		this.ctx.globalCompositeOperation = "source-over";
	}

	/**
	 * Draw all curves
	 */
	private draw(): void {
		this.curves.forEach((curve) => curve.draw());
	}

	/**
	 * Clear the space, interpolate values, calculate new steps and draws
	 * @returns
	 */
	private startDrawCycle(): void {
		this.clear();
		// Interpolate values
		this.lerp("amplitude");
		this.lerp("speed");
		this.draw();
		this.phase = (this.phase + (Math.PI / 2) * this.speed) % (2 * Math.PI);
		if (window.requestAnimationFrame) {
			this.animationFrameId = window.requestAnimationFrame(this.startDrawCycle.bind(this));
		} else {
			this.timeoutId = setTimeout(this.startDrawCycle.bind(this), 20);
		}
	}

	/* API */
	/**
	 * Start the animation
	 */
	public start(): void {
		if (!this.canvas) {
			throw new Error("This instance of SiriWave has been disposed, please create a new instance");
		}
		this.phase = 0;
		// Ensure we don't re-launch the draw cycle
		if (!this.run) {
			this.run = true;
			this.startDrawCycle();
		}
	}

	/**
	 * Stop the animation
	 */
	public stop(): void {
		this.phase = 0;
		this.run = false;
		// Clear old draw cycle on stop
		if (this.animationFrameId) {
			window.cancelAnimationFrame(this.animationFrameId);
		}
		if (this.timeoutId) {
			clearTimeout(this.timeoutId);
		}
	}

	/**
	 * Dispose
	 */
	public dispose(): void {
		this.stop();
		if (this.canvas) {
			this.canvas.remove();
			this.canvas = null;
		}
	}

	/**
	 * Set a new value for a property (interpolated)
	 */
	public set(propertyStr: string, value: number): void {
		this.interpolation[propertyStr] = value;
	}

	/**
	 * Set a new value for the speed property (interpolated)
	 */
	public setSpeed(value: number): void {
		this.set("speed", value);
	}

	/**
	 * Set a new value for the amplitude property (interpolated)
	 */
	public setAmplitude(value: number): void {
		this.set("amplitude", value);
	}

	/**
	 * Set new colors for the waves
	 */
	public setColors(colors: string[]): void {
		if (!this.canvas) {
			throw new Error("This instance of SiriWave has been disposed");
		}

		// 处理颜色（将十六进制转换为RGB格式）
		this.customColors = colors.map(color => {
			// 确保我们总是得到RGB格式
			const rgb = this.hex2rgb(color);
			return rgb || "0,0,0"; // 如果转换失败，使用黑色
		});


		// 更新曲线颜色
		if (this.opt.style === 'ios9') {
			const currentCurves = this.curves as iOS9Curve[];
			const newDefinitions = iOS9Curve.getDefinition(this.customColors);

			if (currentCurves.length > 0) {
				// 确保至少更新一个曲线的颜色
				for (let i = 1; i < Math.min(currentCurves.length, newDefinitions.length); i++) {
					if (newDefinitions[i] && newDefinitions[i].color && currentCurves[i]) {
						currentCurves[i].definition.color = newDefinitions[i].color;
					}
				}
			} else {
				this.curves = newDefinitions.map(def => new iOS9Curve(this, def));
			}
		}
	}
} 